import { useCallback, useMemo, useState } from 'react';

import type { Contact } from '@/api/types/contacts';

import type { SectionDef } from './registry';
import { sectionRegistry, sectionRegistryByKey } from './registry';

const STORAGE_KEY = 'contact-section-order:v1';

function loadOrder(): string[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || !parsed.every((x) => typeof x === 'string')) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persistOrder(order: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
  } catch {
    // ignore quota / privacy-mode failures
  }
}

function reconcileOrder(stored: string[] | null): string[] {
  const known = new Set(sectionRegistry.map((s) => s.key));
  const fromStored = (stored ?? []).filter((k) => known.has(k));
  const missing = sectionRegistry.map((s) => s.key).filter((k) => !fromStored.includes(k));
  return [...fromStored, ...missing];
}

export interface UseSectionLayoutResult {
  visibleSections: SectionDef[];
  emptySections: SectionDef[];
  enableSection: (key: string) => void;
  disableSection: (key: string) => void;
  reorderSections: (nextOrder: string[]) => void;
  isUserEnabled: (key: string) => boolean;
}

export function useSectionLayout(contact: Contact | undefined): UseSectionLayoutResult {
  const [order, setOrder] = useState<string[]>(() => reconcileOrder(loadOrder()));
  const [userEnabled, setUserEnabled] = useState<Set<string>>(() => new Set());

  const enableSection = useCallback((key: string) => {
    setUserEnabled((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const disableSection = useCallback((key: string) => {
    setUserEnabled((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const reorderSections = useCallback((nextOrder: string[]) => {
    const reconciled = reconcileOrder(nextOrder);
    setOrder(reconciled);
    persistOrder(reconciled);
  }, []);

  const { visibleSections, emptySections } = useMemo(() => {
    const visible: SectionDef[] = [];
    const empty: SectionDef[] = [];
    for (const key of order) {
      const def = sectionRegistryByKey[key];
      if (!def) continue;
      const isEmpty = def.isEmpty(contact);
      const include = def.required || !isEmpty || userEnabled.has(key);
      if (include) visible.push(def);
      else empty.push(def);
    }
    return { visibleSections: visible, emptySections: empty };
  }, [order, contact, userEnabled]);

  const isUserEnabled = useCallback((key: string) => userEnabled.has(key), [userEnabled]);

  return {
    visibleSections,
    emptySections,
    enableSection,
    disableSection,
    reorderSections,
    isUserEnabled,
  };
}
